import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Palette, ArrowLeft, FloppyDisk, ArrowCounterClockwise } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, PRESET_COLORS } from '../contexts/ThemeContext';
import AvatarUpload from '../components/AvatarUpload';
import RestTimerSlider from '../components/RestTimerSlider';
import TutorialModal from '../components/TutorialModal';
import { restChime } from '../utils/restChime';
import { downloadHtmlExport } from '../utils/exportHtml';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Colour role definitions — the seven knobs the user can tweak.
const COLOR_ROLES = [
  { key: 'bg',        label: 'Background',         hint: 'App backdrop (inside the container)' },
  { key: 'frame',     label: 'App Frame',          hint: 'Space around the app (outer surround)' },
  { key: 'card',      label: 'Card Surface',       hint: 'Panels and tiles' },
  { key: 'text',      label: 'Body Text',          hint: 'Paragraph and labels' },
  { key: 'muted',     label: 'Muted Text',         hint: 'Small captions' },
  { key: 'primary',   label: 'Primary Accent',     hint: 'Buttons, streak, active' },
  { key: 'secondary', label: 'Secondary Accent',   hint: 'Partner, links, ticks' },
];

const WALLPAPERS = [
  { key: 'none',      label: 'None' },
  { key: 'grain',     label: 'Grain' },
  { key: 'gradient',  label: 'Gradient' },
  { key: 'blueprint', label: 'Blueprint' },
  { key: 'concrete',  label: 'Concrete' },
];

export default function Settings() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { colors, setColors, presets, applyPreset, resetColors } = useTheme();

  const [displayName, setDisplayName] = useState('');
  const [weightUnit, setWeightUnit] = useState('kg');
  const [distanceUnit, setDistanceUnit] = useState('km');
  const [defaultRest, setDefaultRest] = useState(90);
  const [restTimerSound, setRestTimerSound] = useState(true);
  const [wallpaper, setWallpaper] = useState('grain');
  const [draftColors, setDraftColors] = useState(colors);
  const savedColorsRef = useRef(colors); // baseline for dirty comparison
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState('');
  const [showTutorial, setShowTutorial] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { data } = await axios.get(`${API_URL}/export/data`, { withCredentials: true });
      downloadHtmlExport(data);
    } catch (e) {
      console.error('Export failed:', e);
      alert('Export failed — try again in a moment.');
    } finally {
      setExporting(false);
    }
  };

  // Hydrate from user (once, when user id changes). Snapshot the applied
  // colours as the "saved" baseline for dirty detection.
  useEffect(() => {
    if (!user) return;
    setDisplayName(user.display_name || '');
    setWeightUnit(user.weight_unit || 'kg');
    setDistanceUnit(user.distance_unit || 'km');
    setDefaultRest(user.default_rest_seconds || 90);
    setWallpaper(user.wallpaper || 'grain');
    setDraftColors(colors);
    savedColorsRef.current = { ...colors };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Live preview: push draft colours to the DOM without persisting. Uses a
  // shallow equality check inside ThemeContext.setColors so this doesn't loop.
  useEffect(() => {
    setColors(draftColors, { persist: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftColors]);

  // Wallpaper preview — the WallpaperLayer reads user.wallpaper, so we
  // temporarily override the layer's data-wallpaper attribute directly.
  useEffect(() => {
    const layer = document.querySelector('.wallpaper-layer');
    if (layer) layer.setAttribute('data-wallpaper', wallpaper);
  }, [wallpaper]);

  const dirty = useMemo(() => {
    if (!user) return false;
    if (displayName.trim() !== (user.display_name || '')) return true;
    if (weightUnit !== (user.weight_unit || 'kg')) return true;
    if (distanceUnit !== (user.distance_unit || 'km')) return true;
    if (defaultRest !== (user.default_rest_seconds || 90)) return true;
    if (restTimerSound !== (user.rest_timer_sound !== false)) return true;
    if (wallpaper !== (user.wallpaper || 'grain')) return true;
    // Compare draft against the saved baseline (not the live-previewed colors)
    const saved = savedColorsRef.current || {};
    for (const r of COLOR_ROLES) {
      if ((draftColors[r.key] || '').toLowerCase() !== (saved[r.key] || '').toLowerCase()) {
        return true;
      }
    }
    return false;
  }, [displayName, weightUnit, distanceUnit, defaultRest, restTimerSound, wallpaper, draftColors, user]);

  const saveAll = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const payload = {
        display_name: displayName.trim(),
        weight_unit: weightUnit,
        distance_unit: distanceUnit,
        default_rest_seconds: defaultRest,
        wallpaper: wallpaper,
        custom_colors: draftColors,
      };
      const { data } = await axios.put(`${API_URL}/auth/me`, payload, { withCredentials: true });
      setUser(data);
      setColors(draftColors, { persist: true });
      savedColorsRef.current = { ...draftColors };
      setSavedNote('Saved');
    } catch (e) {
      setSavedNote('Save failed');
    } finally {
      setSaving(false);
      setTimeout(() => setSavedNote(''), 1600);
    }
  };

  const discard = () => {
    if (!user) return;
    setDisplayName(user.display_name || '');
    setWeightUnit(user.weight_unit || 'kg');
    setDistanceUnit(user.distance_unit || 'km');
    setDefaultRest(user.default_rest_seconds || 90);
    setRestTimerSound(user.rest_timer_sound !== false);
    setWallpaper(user.wallpaper || 'grain');
    setDraftColors({ ...savedColorsRef.current }); // revert live preview too
  };

  const onAvatarChanged = (url) => {
    setUser({ ...user, avatar_url: url || null });
  };

  const chooseColor = (key, value) => {
    setDraftColors((prev) => ({ ...prev, [key]: value }));
  };

  const applyDraftPreset = (presetKey) => {
    const preset = presets[presetKey];
    if (!preset) return;
    setDraftColors({ ...preset.colors });
  };

  const resetToDefault = () => {
    setDraftColors({ ...presets.chalk.colors });
  };

  return (
    <div className="mobile-container min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-bone/90 backdrop-blur-md border-b border-hairline/60 px-5 py-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => { discard(); navigate(-1); }}
            className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center"
            data-testid="settings-back-button"
            aria-label="Back"
          >
            <ArrowLeft size={18} weight="bold" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-graphite/50 leading-none">
              Profile
            </p>
            <h1 className="text-2xl font-black font-heading uppercase text-graphite leading-none mt-1">
              Settings
            </h1>
          </div>
          {savedNote && (
            <span
              className={`text-[10px] uppercase tracking-[0.15em] font-bold ${
                savedNote === 'Saved' ? 'text-graphite/60' : 'text-userRed'
              }`}
              data-testid="settings-saved-indicator"
            >
              {savedNote}
            </span>
          )}
        </div>
      </div>

      <div className="px-5 pt-5 pb-40 space-y-4">
        {/* Avatar + Name */}
        <section className="ios-card p-5 flex flex-col items-center text-center" data-testid="settings-identity">
          <AvatarUpload user={user} size={104} onUploaded={onAvatarChanged} testId="settings-avatar" />
          <div className="w-full mt-4">
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5 text-left">
              Display Name
            </label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={40}
              inputMode="text"
              enterKeyHint="done"
              autoCapitalize="words"
              className="w-full h-12 px-4 rounded-ios-pill border border-hairline bg-boneWarm focus:outline-none focus:ring-2 focus:ring-graphite text-base"
              placeholder="Your name"
              data-testid="settings-display-name-input"
            />
            <p className="mt-1.5 text-[10px] text-graphite/50 uppercase tracking-[0.16em] font-bold">
              Two names get two initials on your avatar
            </p>
          </div>
        </section>

        {/* Units */}
        <section className="ios-card p-5" data-testid="settings-units">
          <h2 className="text-lg font-black font-heading uppercase text-graphite mb-3">Units</h2>
          <UnitToggle
            label="Weight"
            options={[{ value: 'kg', label: 'Kilograms' }, { value: 'lb', label: 'Pounds' }]}
            value={weightUnit}
            onChange={setWeightUnit}
            testIdPrefix="unit-weight"
          />
          <div className="h-3" />
          <UnitToggle
            label="Distance"
            options={[{ value: 'km', label: 'Kilometres' }, { value: 'mi', label: 'Miles' }]}
            value={distanceUnit}
            onChange={setDistanceUnit}
            testIdPrefix="unit-distance"
          />
        </section>

        {/* Default Rest */}
        <section className="ios-card p-5" data-testid="settings-default-rest">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-lg font-black font-heading uppercase text-graphite">Default Rest</h2>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50">
              Fallback between sets
            </p>
          </div>
          <RestTimerSlider
            value={defaultRest}
            onChange={setDefaultRest}
            testIdPrefix="settings-default-rest"
          />
          {/* Rest chime toggle — plays a soft two-tone bell at 0s */}
          <div className="flex items-center justify-between pt-4 mt-4 border-t border-hairline/60">
            <div className="min-w-0 pr-3">
              <p className="text-sm font-black uppercase tracking-tight text-graphite">
                Rest Chime
              </p>
              <p className="text-[11px] text-graphite/60 mt-0.5">
                Soft bell when the rest timer finishes.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => restChime.preview()}
                className="h-9 px-3 rounded-ios-pill border border-hairline text-[10px] font-black uppercase tracking-[0.18em] text-graphite/70 hover:bg-graphite/5"
                data-testid="preview-rest-chime"
                aria-label="Preview rest chime"
              >
                Preview
              </button>
              <button
                type="button"
                role="switch"
                aria-checked={restTimerSound}
                onClick={() => setRestTimerSound((v) => !v)}
                className={`w-12 h-7 rounded-ios-pill relative transition-colors ${
                  restTimerSound ? 'bg-userRed' : 'bg-graphite/25'
                }`}
                data-testid="rest-sound-toggle"
              >
                <span
                  className={`absolute top-0.5 w-6 h-6 rounded-full bg-bone shadow-ios transition-all ${
                    restTimerSound ? 'left-[22px]' : 'left-0.5'
                  }`}
                />
              </button>
            </div>
          </div>
        </section>

        {/* Help / Onboarding */}
        <section className="ios-card p-5" data-testid="settings-help">
          <h2 className="text-lg font-black font-heading uppercase text-graphite mb-2">Help</h2>
          <p className="text-xs text-graphite/60 mb-3">
            Want a quick refresher on how BRETH works?
          </p>
          <button
            onClick={() => setShowTutorial(true)}
            className="w-full h-12 rounded-ios-pill border border-hairline text-graphite font-black text-xs uppercase tracking-[0.18em] hover:bg-graphite/5"
            data-testid="replay-tutorial-button"
          >
            Replay Getting Started
          </button>
        </section>

        {/* Data Export — a portable HTML of everything */}
        <section className="ios-card p-5" data-testid="settings-export">
          <h2 className="text-lg font-black font-heading uppercase text-graphite mb-2">Export My Data</h2>
          <p className="text-xs text-graphite/60 mb-3">
            Download every workout, set, PB, measurement and goal you've
            logged as a single self-contained HTML file. Open it in any
            browser, upload it to Claude, or host it on your own site — no
            login required to read.
          </p>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="w-full h-12 rounded-ios-pill bg-graphite text-bone font-black text-xs uppercase tracking-[0.18em] disabled:opacity-60"
            data-testid="export-data-button"
          >
            {exporting ? 'Preparing…' : 'Download HTML Export'}
          </button>
        </section>

        {/* Appearance — presets + 6 custom colour pickers */}
        <section className="ios-card p-5" data-testid="settings-appearance">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Palette size={16} weight="fill" className="text-userRed" />
              <h2 className="text-lg font-black font-heading uppercase text-graphite">Appearance</h2>
            </div>
            <button
              onClick={resetToDefault}
              className="text-[10px] uppercase tracking-[0.18em] font-bold text-graphite/50 hover:text-graphite flex items-center gap-1"
              data-testid="reset-colors-button"
            >
              <ArrowCounterClockwise size={11} weight="bold" /> Reset
            </button>
          </div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-2">
            Quick Start
          </p>
          <div className="grid grid-cols-3 gap-1.5 mb-4">
            {Object.entries(presets).map(([key, p]) => (
              <button
                key={key}
                onClick={() => applyDraftPreset(key)}
                className="p-2 rounded-ios border border-hairline hover:border-graphite text-left"
                data-testid={`preset-${key}`}
              >
                <div className="flex gap-0.5 mb-1">
                  {[p.colors.bg, p.colors.card, p.colors.primary, p.colors.secondary].map((c, i) => (
                    <span
                      key={i}
                      className="w-3.5 h-3.5 rounded-full border border-hairline"
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <p className="text-[9px] uppercase tracking-[0.15em] font-black text-graphite truncate">
                  {p.label}
                </p>
              </button>
            ))}
          </div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-2">
            Custom Colours
          </p>
          <div className="space-y-1.5">
            {COLOR_ROLES.map((role) => (
              <ColorPickerRow
                key={role.key}
                label={role.label}
                hint={role.hint}
                value={draftColors[role.key] || '#000000'}
                onChange={(v) => chooseColor(role.key, v)}
                testId={`color-${role.key}`}
              />
            ))}
          </div>

          <div className="h-4" />
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-2">
            Wallpaper
          </p>
          <div className="grid grid-cols-3 gap-1.5" data-testid="wallpaper-picker">
            {WALLPAPERS.map((wp) => (
              <button
                key={wp.key}
                onClick={() => setWallpaper(wp.key)}
                className={`relative p-2 rounded-ios border-2 overflow-hidden text-left h-16 flex items-end ${
                  wallpaper === wp.key ? 'border-userRed' : 'border-hairline hover:border-graphite/40'
                }`}
                data-testid={`wallpaper-${wp.key}`}
              >
                <span
                  className="wallpaper-layer !absolute inset-0 !z-0 rounded-ios"
                  data-wallpaper={wp.key}
                  style={{ position: 'absolute' }}
                />
                <p className="relative text-[9px] uppercase tracking-[0.15em] font-black text-graphite truncate">
                  {wp.label}
                </p>
              </button>
            ))}
          </div>
        </section>
      </div>

      {/* Sticky save bar */}
      <div
        className={`fixed left-0 right-0 z-40 px-5 transition-transform ${
          dirty ? 'translate-y-0' : 'translate-y-[130%]'
        }`}
        style={{ bottom: 'calc(88px + env(safe-area-inset-bottom))' }}
        data-testid="settings-save-bar"
      >
        <div className="mx-auto max-w-md flex items-center gap-2 bg-graphite text-bone rounded-ios-pill shadow-ios px-3 py-2">
          <button
            onClick={discard}
            className="h-11 px-4 rounded-ios-pill text-[11px] uppercase tracking-[0.18em] font-bold text-bone/70 hover:text-bone"
            data-testid="settings-discard-button"
          >
            Discard
          </button>
          <button
            onClick={saveAll}
            disabled={saving}
            className="flex-1 h-11 rounded-ios-pill bg-userRed text-bone font-black text-xs uppercase tracking-[0.18em] flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="settings-save-button"
          >
            <FloppyDisk size={14} weight="bold" />
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {showTutorial && (
        <TutorialModal onClose={() => setShowTutorial(false)} source="settings" />
      )}
</div>
  );
}

function UnitToggle({ label, options, value, onChange, testIdPrefix }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-2">
        {label}
      </p>
      <div className="grid grid-cols-2 gap-2">
        {options.map((o) => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={`h-12 rounded-ios-pill text-xs font-black uppercase tracking-[0.15em] ${
              value === o.value ? 'bg-graphite text-bone' : 'bg-graphite/5 text-graphite/60'
            }`}
            data-testid={`${testIdPrefix}-${o.value}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ColorPickerRow({ label, hint, value, onChange, testId }) {
  return (
    <label
      className="flex items-center gap-3 py-2 px-2 rounded-ios hover:bg-graphite/[0.03] cursor-pointer"
      data-testid={testId}
    >
      <div className="relative flex-shrink-0">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer"
          data-testid={`${testId}-input`}
          aria-label={`Choose ${label} colour`}
        />
        <span
          className="block w-10 h-10 rounded-full border-2 border-hairline shadow-ios"
          style={{ backgroundColor: value }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-black uppercase tracking-tight text-graphite">{label}</p>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-graphite/50">{hint}</p>
      </div>
      <span
        className="text-[10px] uppercase tracking-[0.15em] font-bold text-graphite/50 tabular-nums"
        data-testid={`${testId}-hex`}
      >
        {value.toUpperCase()}
      </span>
    </label>
  );
}
